import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  csvDownloadURL,
  endpoints,
  numericToNumber,
  type CampaignChannelDraft,
  type CampaignResponse,
  type ChannelLifecycle,
  type ManualCreateChannelRequest,
} from "@/lib/api";
import {
  fmtDate,
  fmtNumber,
  fmtPercent,
  fmtRelativeDays,
  fmtTimestamp,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { lifecycleLabel } from "@/components/ui/Badge";
import { CompetitorOverlapCell } from "@/components/CompetitorOverlapCell";
import { ChannelNameCell } from "@/components/channel/ChannelNameCell";
import {
  LifecycleEditCell,
  OutreachStageEditCell,
  QuotedPriceEditCell,
} from "@/components/InlineEditCell";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { Lbl } from "@/components/ui/Lbl";
import { Modal } from "@/components/ui/Modal";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";
import { ChannelDrawer } from "@/components/channel/ChannelDrawer";

// ---------------------------------------------------------------------------
// Channel Library (频道库) — paginated, filterable channel list with a
// click-to-open detail drawer (score + recent videos + decision history).
// Reads /api/channels, /api/channels/:id/score, /:id/videos, /:id/evaluations.
// ---------------------------------------------------------------------------

// DB stores platform code as "twitter" (legacy from Phase 1 schema —
// see internal/platform/adapter.go: PlatformX = "twitter"). Operator-facing
// label stays "X" but the value passed to the API / SQL filter must match
// the DB column.
const PLATFORMS = [
  { value: "", label: "全部平台" },
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X" },
  { value: "tiktok", label: "TikTok" },
  { value: "rss", label: "RSS" },
  { value: "foresight", label: "Foresight" },
  { value: "panews", label: "PA News" },
];

const LIFECYCLES = [
  { value: "", label: "全部状态" },
  { value: "discovered", label: "已发现" },
  { value: "evaluating", label: "评估中" },
  { value: "candidate", label: "候选" },
  { value: "active", label: "活跃" },
  { value: "monitor_only", label: "仅观察" },
  { value: "rejected", label: "已拒绝" },
  { value: "archived", label: "已归档" },
];

const CONTENT_TYPES = [
  { value: "", label: "全部类型" },
  { value: "trading", label: "trading" },
  { value: "education", label: "education" },
  { value: "news", label: "news" },
  { value: "tech", label: "tech" },
  { value: "lifestyle", label: "lifestyle" },
  { value: "other", label: "other" },
];

const PAGE_SIZE = 50;

// v2.0.6 — number of days since last published content above which we flag
// the channel as "沉睡". 90 matches the analytical window (engagement_rate,
// growth_rate_90d, 中位播 are all 90d) — if there's nothing in that window,
// every 90d metric will be empty, so the operator deserves a hint.
const DORMANT_THRESHOLD_DAYS = 90;

// computeDormancyDays returns the integer days since LastContentAt when it's
// older than DORMANT_THRESHOLD_DAYS, or null when the channel is recently
// active. Returns 999 (sentinel = "rendered as ∞") when LastContentAt has
// never been set — a brand-new channel + zero scraped content.
// v2.4.10 — column visibility. 13-column tables exceed cognitive load and
// scroll off on <1400px screens; let the operator pick what to show + remember
// the choice. ALL_COLUMNS is the authoritative ordered list (matches table
// render order); DEFAULT_VISIBLE is the ship-with set chosen so anyone
// landing fresh sees a sensible decision-oriented view.
const COLUMN_DEFS = [
  { key: "name",       label: "名称",              required: true },
  { key: "followers",  label: "粉丝",              required: false },
  { key: "lifecycle",  label: "状态",              required: false },
  { key: "bd",         label: "BD 状态",           required: false },
  { key: "engagement", label: "互动率 (30d)",      required: false },
  { key: "overlap",    label: "竞品重叠 (90d)",    required: false },
  { key: "composite",  label: "综合分",            required: false },
  { key: "campaigns",  label: "投放数",            required: false },
  { key: "price",      label: "报价 (USD)",        required: false },
  { key: "median",     label: "中位播放",          required: false },
  { key: "cpm",        label: "预估 CPM",          required: false },
  { key: "lastContact",label: "最近联络",          required: false },
  { key: "lastContent",label: "最后内容",          required: false },
] as const;
type ColumnKey = (typeof COLUMN_DEFS)[number]["key"];
const DEFAULT_VISIBLE: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  "name", "followers", "lifecycle", "bd",
  "engagement", "overlap", "lastContact", "lastContent",
]);
const COLUMN_VISIBILITY_STORAGE_KEY = "channels.visibleColumns.v1";

function loadVisibleColumns(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as string[];
      const valid = new Set<ColumnKey>();
      const all = new Set(COLUMN_DEFS.map((c) => c.key));
      for (const k of arr) {
        if (all.has(k as ColumnKey)) valid.add(k as ColumnKey);
      }
      // Always include required cols even if localStorage omits them
      for (const c of COLUMN_DEFS) {
        if (c.required) valid.add(c.key);
      }
      return valid;
    }
  } catch {
    // localStorage disabled / SSR / malformed JSON — fall through to defaults
  }
  return new Set(DEFAULT_VISIBLE);
}

function computeDormancyDays(lastContent: string | null | undefined): number | null {
  if (!lastContent) {
    return 999; // never had content; show "沉睡 ∞" so dormancy is visible
  }
  const last = Date.parse(lastContent);
  if (Number.isNaN(last)) {
    return null;
  }
  const days = Math.floor((Date.now() - last) / (1000 * 60 * 60 * 24));
  return days > DORMANT_THRESHOLD_DAYS ? days : null;
}

export default function Channels() {
  const [platform, setPlatform] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [contentType, setContentType] = useState("");
  const [region, setRegion] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  // v2.17.x — '' (default) → follower_count DESC; 'composite' → 综合分 DESC.
  const [sort, setSort] = useState("");
  const [page, setPage] = useState(1);
  const [selectedID, setSelectedID] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  // v2.4.10 — visible columns. useState init runs once; subsequent toggles
  // persist via useEffect.
  const [visibleCols, setVisibleCols] = useState<Set<ColumnKey>>(() => loadVisibleColumns());
  const [showColMenu, setShowColMenu] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(
        COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(Array.from(visibleCols)),
      );
    } catch {
      // localStorage disabled — choice will revert next reload, no fatal
    }
  }, [visibleCols]);
  const toggleCol = (k: ColumnKey) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const isVisible = (k: ColumnKey) => visibleCols.has(k);
  // Bulk-select state. Stored as a Set for O(1) toggle; cleared whenever
  // the filter changes (otherwise users could carry stale selections from
  // a previous view into a bulk action).
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showBulkAddCampaign, setShowBulkAddCampaign] = useState(false);

  // Clear selection whenever filters change — otherwise stale selection
  // from a previous filter view would silently carry into a bulk action.
  useEffect(() => {
    setSelected(new Set());
  }, [platform, lifecycle, contentType, region, includeArchived]);

  const listQ = useQuery({
    queryKey: [
      "channels",
      { platform, lifecycle, content_type: contentType, region, includeArchived, sort, page },
    ],
    queryFn: () =>
      endpoints.channels({
        platform,
        lifecycle,
        content_type: contentType,
        region,
        include_archived: includeArchived,
        sort,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const rows = listQ.data?.data ?? [];
  const hasNext = listQ.data?.next_page !== undefined;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">频道库</h1>
          <span className="text-sm text-muted-foreground">
            第 {page} 页 · 本页 {rows.length} 条
          </span>
          <div className="ml-auto flex items-center gap-2">
            {/* v2.4.10 — column visibility menu. Operator's choice persists
                via localStorage so a daily-use view stays customized. */}
            <div className="relative">
              <button
                onClick={() => setShowColMenu((s) => !s)}
                className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-2 text-xs font-medium transition-colors hover:bg-muted"
                title="选择显示的列"
              >
                ⚙ 列 ({visibleCols.size}/{COLUMN_DEFS.length})
              </button>
              {showColMenu && (
                <div className="absolute right-0 top-9 z-20 w-56 rounded-md border border-border bg-background p-2 text-xs shadow-lg">
                  <div className="mb-1 px-1 text-[10px] text-muted-foreground">
                    勾选要显示的列(自动记住)
                  </div>
                  {COLUMN_DEFS.map((c) => (
                    <label
                      key={c.key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={visibleCols.has(c.key)}
                        disabled={c.required}
                        onChange={() => toggleCol(c.key)}
                      />
                      <span className={c.required ? "text-muted-foreground" : ""}>
                        {c.label}
                        {c.required && <span className="ml-1 text-[10px]">(锁定)</span>}
                      </span>
                    </label>
                  ))}
                  <button
                    onClick={() => {
                      setVisibleCols(new Set(DEFAULT_VISIBLE));
                      setShowColMenu(false);
                    }}
                    className="mt-1 w-full rounded px-2 py-1 text-left text-[10px] text-muted-foreground hover:bg-muted"
                  >
                    恢复默认
                  </button>
                </div>
              )}
            </div>
            {/* v2.4.0 — export current filters as CSV. Uses an <a download>
                so the browser handles the file save dialog natively. */}
            <a
              href={csvDownloadURL("/api/channels", {
                platform,
                lifecycle,
                content_type: contentType,
                region,
                include_archived: includeArchived,
              })}
              className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted"
              title="导出当前筛选的全部频道(最多 50000 条)"
            >
              ↓ 导出 CSV
            </a>
            {/* v2.3.2 — CSV 导入 moved here from sidebar (top-level nav was
                the wrong home; this is a 频道库 entry point). */}
            <Link
              to="/import"
              className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium transition-colors hover:bg-muted"
            >
              CSV 批量导入
            </Link>
            <Button size="sm" onClick={() => setShowAdd(true)}>
              + 手动添加频道
            </Button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={platform} onChange={(e) => { setPlatform(e.target.value); setPage(1); }} className="w-36">
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
          <Select value={lifecycle} onChange={(e) => { setLifecycle(e.target.value); setPage(1); }} className="w-44">
            {LIFECYCLES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
          <Select value={contentType} onChange={(e) => { setContentType(e.target.value); setPage(1); }} className="w-36">
            {CONTENT_TYPES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Select>
          <Input
            placeholder="区域 (eg. en, cn-zh)"
            value={region}
            onChange={(e) => { setRegion(e.target.value); setPage(1); }}
            className="w-44"
          />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }}
            />
            含归档
          </label>
          {/* v2.17.x — optional quality-weighted ordering. Default stays
              follower_count so the established view is unchanged. */}
          <Select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="w-36"
            title="排序方式：综合分 = 质量加权（粉丝/互动/活跃/竞品重叠）"
          >
            <option value="">排序：粉丝数</option>
            <option value="composite">排序：综合分</option>
          </Select>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || listQ.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || listQ.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto bg-background">
          {listQ.isLoading && (
            <p className="p-4 text-sm text-muted-foreground">加载中…</p>
          )}
          {listQ.isError && (
            <p className="p-4 text-sm text-destructive">
              加载失败：{(listQ.error as Error).message}
            </p>
          )}
          {listQ.data && rows.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">没有匹配的频道</p>
          )}
          {rows.length > 0 && selected.size > 0 && (
            <BulkActionBar
              selected={selected}
              clearSelection={() => setSelected(new Set())}
              filterParams={{
                platform,
                lifecycle,
                content_type: contentType,
                region,
                include_archived: includeArchived,
              }}
              onSelectAllFiltered={(ids) => setSelected(new Set(ids))}
              onAddToCampaign={() => setShowBulkAddCampaign(true)}
            />
          )}
          {rows.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH className="w-8">
                    <input
                      type="checkbox"
                      title="本页全选"
                      checked={rows.length > 0 && rows.every((r) => selected.has(r.ID))}
                      ref={(el) => {
                        if (el) {
                          const someChecked = rows.some((r) => selected.has(r.ID));
                          const allChecked = rows.every((r) => selected.has(r.ID));
                          el.indeterminate = someChecked && !allChecked;
                        }
                      }}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) {
                          rows.forEach((r) => next.add(r.ID));
                        } else {
                          rows.forEach((r) => next.delete(r.ID));
                        }
                        setSelected(next);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TH>
                  {isVisible("name")       && <TH>名称</TH>}
                  {isVisible("followers")  && <TH>粉丝</TH>}
                  {isVisible("lifecycle")  && <TH>状态</TH>}
                  {isVisible("bd")         && <TH>BD 状态</TH>}
                  {isVisible("engagement") && <TH className="text-right">互动率 (30d)</TH>}
                  {isVisible("overlap")    && <TH className="text-right">竞品重叠 (90d)</TH>}
                  {isVisible("composite") && (
                    <TH className="text-right" title="质量加权综合分 (0-100)：粉丝/互动/活跃/竞品重叠 归一化加权（权重可在 config 调，growth 不计）。空 = 样本不足">
                      综合分
                    </TH>
                  )}
                  {isVisible("campaigns")  && <TH className="text-right">投放数</TH>}
                  {isVisible("price")      && <TH className="text-right">报价 (USD)</TH>}
                  {isVisible("median") && (
                    <TH className="text-right" title="中位数（不受爆款 outliers 影响）— 90d 内每条 video/tweet 的 view_count 中位数">
                      中位播 (90D)
                    </TH>
                  )}
                  {isVisible("cpm")        && <TH className="text-right">预估 CPM</TH>}
                  {isVisible("lastContact")&& <TH>最近联络</TH>}
                  {isVisible("lastContent")&& <TH>最后内容</TH>}
                </TR>
              </THead>
              <TBody>
                {rows.map((c) => {
                  const eng = numericToNumber(c.EngagementRate);
                  const comp = numericToNumber(c.CompositeScore);
                  const isSelected = selected.has(c.ID);
                  const quote = numericToNumber(c.QuotedPriceUsd);
                  // v2.0.6 — was AvgViews90d (mean). Now median — robust to
                  // viral outliers that distorted X channel averages.
                  const medianViews = c.MedianViews90d ?? null;
                  // 预估 CPM = 报价 * 1000 / 中位播. Only meaningful when both
                  // are present; otherwise show dash.
                  const estCPM =
                    quote !== null && medianViews && medianViews > 0
                      ? (quote * 1000) / medianViews
                      : null;
                  // v2.0.6 — dormancy: no content in 90d (or never). Lets the
                  // operator immediately spot why a channel has empty metrics.
                  const dormantDays = computeDormancyDays(c.LastContentAt);
                  return (
                    <TR
                      key={c.ID}
                      onClick={() => setSelectedID(c.ID)}
                      className={cn(
                        "cursor-pointer",
                        selectedID === c.ID && "bg-muted",
                        isSelected && "bg-primary/5",
                      )}
                    >
                      <TD className="w-8" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(c.ID);
                            else next.delete(c.ID);
                            setSelected(next);
                          }}
                        />
                      </TD>
                      {isVisible("name") && (
                        <TD>
                          <ChannelNameCell channel={c} dormantDays={dormantDays} />
                        </TD>
                      )}
                      {isVisible("followers") && <TD>{fmtNumber(c.FollowerCount)}</TD>}
                      {isVisible("lifecycle") && (
                        <TD>
                          <LifecycleEditCell channel={c} />
                        </TD>
                      )}
                      {isVisible("bd") && (
                        <TD>
                          <OutreachStageEditCell channel={c} />
                        </TD>
                      )}
                      {isVisible("engagement") && (
                        <TD className="text-right text-xs">
                          {eng !== null ? fmtPercent(eng, 2) : <span className="text-muted-foreground">—</span>}
                        </TD>
                      )}
                      {isVisible("overlap") && (
                        <TD className="text-right">
                          <CompetitorOverlapCell channelID={c.ID} overlap={c.CompetitorOverlap} />
                        </TD>
                      )}
                      {isVisible("composite") && (
                        <TD className="text-right text-xs">
                          {comp !== null ? (
                            <span className="font-medium tabular-nums">{comp.toFixed(1)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TD>
                      )}
                      {isVisible("campaigns") && (
                        <TD className="text-right tabular-nums">
                          {c.ActiveCampaignCount && c.ActiveCampaignCount > 0
                            ? c.ActiveCampaignCount
                            : <span className="text-muted-foreground">—</span>}
                        </TD>
                      )}
                      {isVisible("price") && (
                        <TD className="text-right">
                          <QuotedPriceEditCell channel={c} current={quote} />
                        </TD>
                      )}
                      {isVisible("median") && (
                        <TD className="text-right tabular-nums">
                          {medianViews
                            ? fmtNumber(medianViews)
                            : <span className="text-muted-foreground">—</span>}
                        </TD>
                      )}
                      {isVisible("cpm") && (
                        <TD className="text-right tabular-nums">
                          {estCPM !== null
                            ? `$${estCPM.toFixed(2)}`
                            : <span className="text-muted-foreground">—</span>}
                        </TD>
                      )}
                      {isVisible("lastContact") && (
                        <TD className="text-xs">
                          {c.LastContactedAt ? (
                            <span title={fmtTimestamp(c.LastContactedAt) ?? ""}>
                              {fmtRelativeDays(c.LastContactedAt)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TD>
                      )}
                      {isVisible("lastContent") && (
                        <TD className="text-xs text-muted-foreground">
                          {fmtDate(c.LastContentAt)}
                        </TD>
                      )}
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </div>

        {selectedID !== null && (
          <ChannelDrawer
            channelID={selectedID}
            onClose={() => setSelectedID(null)}
          />
        )}
      </div>

      {showAdd && (
        <AddChannelModal
          onClose={() => setShowAdd(false)}
          onCreated={(ch) => {
            setShowAdd(false);
            listQ.refetch();
            setSelectedID(ch.id);
          }}
        />
      )}

      {showBulkAddCampaign && (
        <BulkAddCampaignDialog
          channelIDs={Array.from(selected)}
          onClose={() => setShowBulkAddCampaign(false)}
          onAdded={() => {
            setShowBulkAddCampaign(false);
            setSelected(new Set());
            listQ.refetch();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkActionBar — sticky toolbar shown when N rows are selected. Hosts the
// "select all matching filter" expansion + all bulk actions.
// ---------------------------------------------------------------------------

function BulkActionBar({
  selected,
  clearSelection,
  filterParams,
  onSelectAllFiltered,
  onAddToCampaign,
}: {
  selected: Set<number>;
  clearSelection: () => void;
  filterParams: {
    platform: string;
    lifecycle: string;
    content_type: string;
    region: string;
    include_archived: boolean;
  };
  onSelectAllFiltered: (ids: number[]) => void;
  onAddToCampaign: () => void;
}) {
  const qc = useQueryClient();
  const ids = Array.from(selected);

  const expandMut = useMutation({
    mutationFn: () => endpoints.channelIDs(filterParams),
    onSuccess: (data) => {
      onSelectAllFiltered(data.ids);
      if (data.truncated) {
        alert(`筛选下还有更多，已选中前 ${data.count} 个（上限 5000）`);
      }
    },
  });

  const lifecycleMut = useMutation({
    mutationFn: (status: ChannelLifecycle) => endpoints.bulkChannelLifecycle(ids, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      clearSelection();
    },
    onError: (err) => alert((err as Error).message),
  });

  const reevalMut = useMutation({
    mutationFn: () => endpoints.bulkChannelReevaluate(ids),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["evaluation-tasks"] });
      alert(
        `${data.queued.length} 个已入评估队列，${data.skipped.length} 个已有 pending 任务跳过${
          data.failed?.length ? `，${data.failed.length} 个失败` : ""
        }`,
      );
      clearSelection();
    },
    onError: (err) => alert((err as Error).message),
  });

  // v1.4.5 — sync refresh on selected channels only. Different from /system
  // manual cron buttons (which run on ALL eligible channels). Common case:
  // just added 10 KOLs, want fresh data for those 10 without waiting for
  // 00:00 cron tick or burning quota on the full fleet.
  //
  // v2.3.2 — split into two paths:
  //   1. 增量刷新 (default): only pulls videos newer than last_content_at —
  //      cheap, matches what the cron does. The common case.
  //   2. 回拉历史 (explicit): full lookback window, for backfill scenarios
  //      (newly added KOL, corrupted watermark).
  const refreshMut = useMutation({
    mutationFn: (opts?: { lookbackDays?: number; incremental?: boolean }) =>
      endpoints.refreshChannels(ids, opts),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      const ok = data.results.filter((r) => r.ok).length;
      const failed = data.results.filter((r) => !r.ok).length;
      const videos = data.results.reduce((a, r) => a + (r.videos_upserted ?? 0), 0);
      const stats = data.results.reduce((a, r) => a + (r.stats_refreshed ?? 0), 0);
      // v1.4.9 — clarify that detection only scans UNDETECTED videos here.
      // For already-detected videos to be re-scanned (e.g. after adding a
      // new exchange), use 交易所页面 → 重新检测历史.
      const scanned = data.detection_videos_scanned ?? 0;
      const detection = data.detection_ran
        ? scanned > 0
          ? `\n推广检测：扫描 ${scanned} 个未检测内容，命中 ${data.detection_sponsorships ?? 0} 条`
          : `\n推广检测：暂无未检测内容（已检测的不重复扫；要按新规则重扫历史请去交易所页面）`
        : "";
      alert(
        `${ok} 个频道刷新成功\n内容入库 ${videos} · 统计刷新 ${stats}${
          failed > 0 ? `\n${failed} 个失败` : ""
        }${detection}`,
      );
      clearSelection();
    },
    onError: (err) => alert((err as Error).message),
  });

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-primary/30 bg-primary/5 px-4 py-2 text-sm">
      <span className="font-medium">已选 {selected.size} 个</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => expandMut.mutate()}
        disabled={expandMut.isPending}
      >
        {expandMut.isPending ? "查询中…" : "选中当前筛选下全部"}
      </Button>
      <span className="mx-1 text-muted-foreground">|</span>
      <BulkLifecycleMenu
        onPick={(s) => {
          if (
            confirm(
              s === "archived"
                ? `归档 ${selected.size} 个频道？历史保留，列表默认看不到。`
                : `把 ${selected.size} 个频道改为${lifecycleLabel(s)}？`,
            )
          ) {
            lifecycleMut.mutate(s);
          }
        }}
        pending={lifecycleMut.isPending}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => refreshMut.mutate(undefined)}
        disabled={refreshMut.isPending || selected.size > 50}
        title={
          selected.size > 50
            ? "单次最多 50 个"
            : "增量刷新（只拉每个频道 last_content_at 之后的新内容 + 刷统计）"
        }
      >
        {refreshMut.isPending ? "刷新中…" : "增量刷新"}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          const input = prompt(
            `回拉 ${selected.size} 个频道的历史数据（忽略 last_content_at 水位）。\n用于：新加的 KOL backfill、water mark 异常、按新规则重检测。\nLookback 天数（默认 90，最大 730，新加 KOL 建议 365）：`,
            "90",
          );
          if (input === null) return; // cancelled
          const days = input.trim() === "" ? 90 : Number(input);
          if (Number.isNaN(days) || days < 1 || days > 730) {
            alert("请输入 1-730 的整数");
            return;
          }
          refreshMut.mutate({
            incremental: false,
            lookbackDays: days === 90 ? undefined : days,
          });
        }}
        disabled={refreshMut.isPending || selected.size > 50}
        title={selected.size > 50 ? "单次最多 50 个" : "全量回拉，比增量刷新慢很多"}
      >
        回拉历史…
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          if (confirm(`为 ${selected.size} 个频道创建评估任务？已有 pending 任务的会跳过。`)) {
            reevalMut.mutate();
          }
        }}
        disabled={reevalMut.isPending}
      >
        {reevalMut.isPending ? "处理中…" : "批量重评"}
      </Button>
      <Button size="sm" variant="outline" onClick={onAddToCampaign}>
        加入投放…
      </Button>
      <Button size="sm" variant="ghost" onClick={clearSelection} className="ml-auto">
        取消选择
      </Button>
    </div>
  );
}

function BulkLifecycleMenu({
  onPick,
  pending,
}: {
  onPick: (s: ChannelLifecycle) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const options: ChannelLifecycle[] = [
    "discovered",
    "evaluating",
    "candidate",
    "active",
    "monitor_only",
    "rejected",
    "archived",
  ];
  return (
    <div className="relative">
      <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} disabled={pending}>
        改状态 ▾
      </Button>
      {open && (
        <div className="absolute z-20 mt-1 rounded-md border border-border bg-background shadow-md">
          {options.map((s) => (
            <button
              key={s}
              className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              onClick={() => {
                setOpen(false);
                onPick(s);
              }}
            >
              {lifecycleLabel(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkAddCampaignDialog — pick an existing campaign + apply default referral
// to all selected channels in one shot. Each row defaults to no referral_code
// / kol_uid; if the operator wants per-KOL nuance they can edit on the
// campaign detail page after the bulk add lands.
// ---------------------------------------------------------------------------

function BulkAddCampaignDialog({
  channelIDs,
  onClose,
  onAdded,
}: {
  channelIDs: number[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [campaignID, setCampaignID] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const campaignsQ = useQuery({
    queryKey: ["campaigns-for-bulk-add"],
    queryFn: () => endpoints.campaigns({ limit: 200 }),
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!campaignID) throw new Error("请选择投放");
      const drafts: CampaignChannelDraft[] = channelIDs.map((id) => ({ channel_id: id }));
      return endpoints.bulkAddCampaignChannels(campaignID, drafts);
    },
    onSuccess: (data) => {
      alert(
        `成功加入 ${data.added.length} 个；${data.skipped.length} 个已在投放里跳过${
          data.failed?.length ? `，${data.failed.length} 个失败` : ""
        }`,
      );
      onAdded();
    },
    onError: (err) => setError((err as Error).message),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-lg font-semibold">把 {channelIDs.length} 个 KOL 加入投放</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          每个 KOL 默认不带 referral_code / kol_uid / 金额，加完之后在投放详情页可逐个编辑。
        </p>
        <label className="mb-2 block text-xs text-muted-foreground">选择投放</label>
        <Select
          value={campaignID ?? ""}
          onChange={(e) => setCampaignID(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">— 选投放 —</option>
          {(campaignsQ.data?.data ?? []).map((c: CampaignResponse) => (
            <option key={c.id} value={c.id}>
              #{c.id} · {c.name} · {c.channel_count} KOL · {c.status}
            </option>
          ))}
        </Select>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!campaignID || mutation.isPending}>
            {mutation.isPending ? "处理中…" : "加入"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddChannelModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (ch: { id: number }) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ManualCreateChannelRequest>({
    platform: "youtube",
    url_or_handle: "",
    name: "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: ManualCreateChannelRequest) => endpoints.createManualChannel(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      onCreated({ id: created.id });
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.url_or_handle.trim() || !form.name.trim()) {
      return setError("URL/handle 和名称都是必填");
    }
    mutation.mutate({
      ...form,
      url_or_handle: form.url_or_handle.trim(),
      name: form.name.trim(),
    });
  }

  function set<K extends keyof ManualCreateChannelRequest>(
    k: K,
    v: ManualCreateChannelRequest[K],
  ) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <Modal
      title="手动添加频道"
      onClose={onClose}
      onSubmit={submit}
      error={error}
      submitLabel="添加"
      submittingLabel="添加中…"
      submitting={mutation.isPending}
    >
      <p className="mb-3 text-xs text-muted-foreground">
        后端会去对应平台 adapter 解析 URL → 频道 ID，并写入 channels 表。
        {form.platform === "youtube" && "YouTube 会消耗 1 个配额。"}
        {form.platform === "twitter" && "X 会从已加的 cookies pool 抓取。"}
        {form.platform === "rss" && "RSS 直接 HTTP 拉 feed，无配额。"}
        {form.platform === "foresight" && "Foresight 走本机 sidecar（curl_cffi 绕 EdgeOne）。"}
        {form.platform === "panews" && "PA News 走本机 sidecar（同 Foresight 共用）。"}
        粉丝数等指标可留空，下一次 scheduler tick 自动拉。
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Lbl label="平台 *">
          <Select
            value={form.platform}
            onChange={(e) => set("platform", e.target.value as ManualCreateChannelRequest["platform"])}
          >
            <option value="youtube">YouTube</option>
            <option value="twitter">X (Twitter)</option>
            <option value="tiktok">TikTok</option>
            <option value="rss">RSS / Medium / Substack</option>
            <option value="foresight">Foresight News (专栏)</option>
            <option value="panews">PA News (专栏)</option>
          </Select>
        </Lbl>
        <Lbl label="名称 *">
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="频道显示名（操作员侧）"
            autoFocus
          />
        </Lbl>
        <Lbl label="URL 或 @handle *" full>
          <Input
            value={form.url_or_handle}
            onChange={(e) => set("url_or_handle", e.target.value)}
            placeholder={
              form.platform === "youtube"
                ? "https://www.youtube.com/@SomeKOL  或  @SomeKOL"
                : form.platform === "twitter"
                ? "https://x.com/SomeKOL  或  @SomeKOL"
                : form.platform === "tiktok"
                ? "https://www.tiktok.com/@SomeKOL  或  @SomeKOL"
                : "URL 或 @handle"
            }
          />
        </Lbl>
        <Lbl label="粉丝数（可选）">
          <Input
            type="number"
            value={form.follower_count ?? ""}
            onChange={(e) => set("follower_count", e.target.value ? Number(e.target.value) : undefined)}
          />
        </Lbl>
        <Lbl label="累计曝光（可选）">
          <Input
            type="number"
            value={form.total_view_count ?? ""}
            onChange={(e) => set("total_view_count", e.target.value ? Number(e.target.value) : undefined)}
            placeholder="YouTube 总播放 / X 总曝光"
          />
        </Lbl>
        <Lbl label="内容类型">
          <Input
            value={form.content_type ?? ""}
            onChange={(e) => set("content_type", e.target.value)}
            placeholder="review_analysis / education / news ..."
          />
        </Lbl>
        <Lbl label="区域">
          <Input
            value={form.region ?? ""}
            onChange={(e) => set("region", e.target.value)}
            placeholder="en / cn-zh / global ..."
          />
        </Lbl>
      </div>
    </Modal>
  );
}
